import type { GameScore, PlayedFixture } from "./standings.js";
import type { Fixture } from "./types.js";

/**
 * Swiss: a fixed number of rounds, each paired from the table the last one left.
 *
 * Everybody plays every round (bar one bye in an odd field), nobody meets the same opponent
 * twice, and each round puts players on the same number of wins against each other — so after
 * a handful of rounds the table has sorted a field far too big for a round robin, without
 * knocking anybody out. Singles only: a side is one person.
 *
 * Like mexicano, only one round can honestly exist at a time; this generates exactly one and
 * the caller appends it. Round one has no table, so the caller passes the seeding order, and it
 * falls out of the same rule as every other round: everybody is on no wins, one group, top half
 * against bottom half.
 */

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

export interface SwissRow {
  memberId: string;
  /** Matches won, a bye counting as one. */
  wins: number;
  /** Matches played, a bye counting as one. */
  played: number;
  /** Byes had. Never more than one while the round count stays within the field. */
  byes: number;
  /** The sum of the wins of everyone this player has actually played. A bye adds nothing. */
  buchholz: number;
  gameDiff: number;
  pointDiff: number;
}

/**
 * The Swiss table: wins, then Buchholz, then game difference, then point difference, then
 * member id, so the same results always give the same order.
 *
 * Wins first because that is what a Swiss round pairs by and what a qualifier is decided on.
 * Buchholz next because it is the one tiebreak that measures what a Swiss draw actually varies
 * — who you were drawn against. Lives here, not in the app, because two things read it: the
 * standings screen, and the next round's pairing. Two versions would disagree in exactly the
 * case somebody argues about.
 */
export function rankSwiss(played: PlayedFixture[], byes: string[]): SwissRow[] {
  const rows = new Map<string, SwissRow & { opponents: string[] }>();
  const row = (memberId: string) => {
    let entry = rows.get(memberId);
    if (!entry) {
      entry = {
        memberId,
        wins: 0,
        played: 0,
        byes: 0,
        buchholz: 0,
        gameDiff: 0,
        pointDiff: 0,
        opponents: [],
      };
      rows.set(memberId, entry);
    }
    return entry;
  };

  for (const fixture of played) {
    const a = fixture.playersA?.[0];
    const b = fixture.playersB?.[0];
    if (!fixture.result || !a || !b) continue;

    const tally = countGames(fixture.result.games);
    const sides = [
      {
        me: row(a),
        them: b,
        games: tally.gamesA - tally.gamesB,
        points: tally.pointsA - tally.pointsB,
        won: "A",
      },
      {
        me: row(b),
        them: a,
        games: tally.gamesB - tally.gamesA,
        points: tally.pointsB - tally.pointsA,
        won: "B",
      },
    ] as const;

    for (const side of sides) {
      side.me.played += 1;
      side.me.gameDiff += side.games;
      side.me.pointDiff += side.points;
      side.me.opponents.push(side.them);
      if (fixture.result.winner === side.won) side.me.wins += 1;
    }
  }

  for (const memberId of byes) {
    const entry = row(memberId);
    entry.played += 1;
    entry.wins += 1;
    entry.byes += 1;
  }

  // After every result is in, so an opponent's later wins count too.
  for (const entry of rows.values()) {
    entry.buchholz = entry.opponents.reduce((sum, id) => sum + (rows.get(id)?.wins ?? 0), 0);
  }

  return [...rows.values()]
    .sort(
      (x, y) =>
        y.wins - x.wins ||
        y.buchholz - x.buchholz ||
        y.gameDiff - x.gameDiff ||
        y.pointDiff - x.pointDiff ||
        x.memberId.localeCompare(y.memberId),
    )
    .map(({ opponents: _opponents, ...rest }) => rest);
}

function countGames(games: GameScore[]) {
  let gamesA = 0;
  let gamesB = 0;
  let pointsA = 0;
  let pointsB = 0;
  for (const game of games) {
    pointsA += game.a;
    pointsB += game.b;
    if (game.a > game.b) gamesA += 1;
    else if (game.b > game.a) gamesB += 1;
  }
  return { gamesA, gamesB, pointsA, pointsB };
}

// ---------------------------------------------------------------------------
// The round
// ---------------------------------------------------------------------------

/** One player going into a round, in table order. */
export interface SwissPlayer {
  memberId: string;
  /** Wins so far, byes included. Players on the same number form a group. */
  wins: number;
}

export interface SwissHistory {
  /** Everyone each player has already played. */
  opponents: ReadonlyMap<string, ReadonlySet<string>>;
  /** Everyone who has already had a bye. */
  hadBye: ReadonlySet<string>;
}

export interface SwissRound {
  /** Matches in pairing order, top of the table first, then the bye. 1-based; the caller offsets. */
  fixtures: Fixture[];
  /** Who has the bye, or null in an even field. */
  bye: string | null;
  /** Pairs that have met before. Empty unless no rematch-free round exists. */
  rematches: [string, string][];
}

/**
 * Steps one search may take before giving up on a rematch budget and trying the next. Fields
 * here are a few dozen at most, where a solution is found in hundreds of steps; this is only
 * the guarantee that a pathological round still finishes.
 */
const STEP_LIMIT = 200_000;

/**
 * One round, from the table as it stands.
 *
 * `players` is the table, best first — the seeding order for round one. Pairing is a
 * depth-first search: the highest unpaired player takes the first opponent in their order of
 * preference that still lets the rest of the round be paired, and backtracks when it does not.
 * Preference is what makes the result a Swiss: within a group, the top half against the bottom
 * half (1 v 5, 2 v 6 … in a group of eight); then the rest of the group; then the groups below,
 * nearest first, which is how the lowest player of an odd group floats down.
 *
 * Rematches are searched for with a budget: none at all first, and only if no such round
 * exists, one, then two. So a rematch happens only when it cannot be avoided, and as few as
 * possible — and the caller is told who, so the organiser sees it before saving.
 */
export function swissRound(
  players: SwissPlayer[],
  history: SwissHistory,
  round: number,
): SwissRound {
  if (players.length < 2) throw new Error("a Swiss round needs at least two players");
  if (round < 1) throw new Error("rounds are numbered from 1");

  const met = (x: string, y: string) => history.opponents.get(x)?.has(y) ?? false;

  // The bye, in an odd field: the lowest-placed player who has not had one, moving up only if
  // giving it to them would force a rematch on everybody else.
  const byeCandidates: (SwissPlayer | null)[] =
    players.length % 2 === 0
      ? [null]
      : [...players].reverse().filter((player) => !history.hadBye.has(player.memberId));
  if (byeCandidates.length === 0) {
    // Everybody has had one: only possible past entrants − 1 rounds, which the caller refuses.
    // Still answer rather than throw — the lowest-placed player, again.
    byeCandidates.push(players[players.length - 1] as SwissPlayer);
  }

  for (let budget = 0; budget <= Math.floor(players.length / 2); budget++) {
    for (const bye of byeCandidates) {
      const field = bye ? players.filter((player) => player !== bye) : players;
      const pairs = pairField(field, met, budget);
      if (pairs) return toRound(pairs, bye?.memberId ?? null, met, round);
    }
  }

  // Unreachable: with a budget of every pair, the first candidate always pairs.
  throw new Error("could not pair this round");
}

/**
 * Pair an even field with at most `budget` rematches, or null. Each player's preference order
 * is fixed up front from the groups at the start of the round.
 */
function pairField(
  field: SwissPlayer[],
  met: (x: string, y: string) => boolean,
  budget: number,
): [SwissPlayer, SwissPlayer][] | null {
  const preference = preferences(field);
  const paired = new Set<SwissPlayer>();
  const pairs: [SwissPlayer, SwissPlayer][] = [];
  let steps = 0;

  // Every remaining player with nobody left they have not met will need a rematch, and one
  // rematch covers at most two of them. More than the budget allows means this branch is dead,
  // which is what turns a late round with no clean answer from an exhaustive search into a
  // quick refusal and lets the next budget be tried.
  const hopeless = (left: number): boolean => {
    let stuck = 0;
    for (const player of field) {
      if (paired.has(player)) continue;
      const free = field.some(
        (other) => other !== player && !paired.has(other) && !met(player.memberId, other.memberId),
      );
      if (!free && ++stuck > 2 * left) return true;
    }
    return false;
  };

  const search = (left: number): boolean => {
    if (++steps > STEP_LIMIT) return false;
    const top = field.find((player) => !paired.has(player));
    if (!top) return true;
    if (hopeless(left)) return false;

    paired.add(top);
    for (const other of preference.get(top) as SwissPlayer[]) {
      if (paired.has(other)) continue;
      const cost = met(top.memberId, other.memberId) ? 1 : 0;
      if (cost > left) continue;

      paired.add(other);
      pairs.push([top, other]);
      if (search(left - cost)) return true;
      pairs.pop();
      paired.delete(other);
    }
    paired.delete(top);
    return false;
  };

  return search(budget) ? pairs : null;
}

/**
 * Each player's opponents in the order they should be tried.
 *
 * Within their own group: the other half first — for the i-th of the top half, the i-th of the
 * bottom half, then its neighbours outwards — then their own half. Then every lower group, in
 * table order. A player only ever looks down: whoever is highest unpaired is the one choosing.
 */
function preferences(field: SwissPlayer[]): Map<SwissPlayer, SwissPlayer[]> {
  const groups: SwissPlayer[][] = [];
  for (const player of field) {
    const last = groups[groups.length - 1];
    if (last && last[0]?.wins === player.wins) last.push(player);
    else groups.push([player]);
  }

  const order = new Map<SwissPlayer, SwissPlayer[]>();
  groups.forEach((group, g) => {
    const half = Math.floor(group.length / 2);
    const below = groups.slice(g + 1).flat();

    group.forEach((player, i) => {
      const topHalf = i < half;
      const target = topHalf ? i + half : i - half;
      const others = group.filter((other) => other !== player);
      const opposite = others.filter((other) => group.indexOf(other) < half !== topHalf);
      const same = others.filter((other) => group.indexOf(other) < half === topHalf);
      const byDistance = (list: SwissPlayer[]) =>
        [...list].sort(
          (x, y) =>
            Math.abs(group.indexOf(x) - target) - Math.abs(group.indexOf(y) - target) ||
            group.indexOf(x) - group.indexOf(y),
        );

      order.set(player, [...byDistance(opposite), ...byDistance(same), ...below]);
    });
  });
  return order;
}

function toRound(
  pairs: [SwissPlayer, SwissPlayer][],
  bye: string | null,
  met: (x: string, y: string) => boolean,
  round: number,
): SwissRound {
  const fixtures: Fixture[] = pairs.map(([a, b], at) => ({
    round,
    sequence: at + 1,
    sideA: { kind: "players", memberIds: [a.memberId] },
    sideB: { kind: "players", memberIds: [b.memberId] },
  }));
  if (bye) {
    fixtures.push({
      round,
      sequence: fixtures.length + 1,
      sideA: { kind: "players", memberIds: [bye] },
      sideB: { kind: "bye" },
    });
  }

  return {
    fixtures,
    bye,
    rematches: pairs
      .filter(([a, b]) => met(a.memberId, b.memberId))
      .map(([a, b]) => [a.memberId, b.memberId]),
  };
}

/** Enough rounds to separate a single leader: the smallest r with 2^r at least the field. */
export const suggestedSwissRounds = (entrants: number): number =>
  entrants < 2 ? 1 : Math.ceil(Math.log2(entrants));

/** The most rounds a field can play without anybody meeting twice or having two byes. */
export const maxSwissRounds = (entrants: number): number => Math.max(1, entrants - 1);
